import { API } from 'aws-amplify';
import { Auth } from 'aws-amplify';
import {
  GetTimelapseConfigurationResult,
  GetProjectResult,
  ProjectCreateRequest,
  ProjectUpdateRequest,
  GetScenariosResult,
  ScenarioConfigurationCreateRequest,
  ScenarioConfigurationCreateResult,
  ScenarioConfigurationUpdateRequest,
} from './projects.interfaces';
import { Project, serializer as projectSerializer } from '../../models/project';
import { Blur, serializer as blurSerializer } from '../../models/blur';
import { Video, serializer as videoSerializer } from '../../models/video';
import { TimelapseConfiguration } from '../../models/timelapseconfiguration';
import { Scenario, serializer as scenarioSerializer } from '../../models/scenario';
import { Storage } from 'aws-amplify';

export class ProjectService {
  public constructor(private readonly apiName: string) {}

  public async getMyProject(): Promise<Project[]> {
    return API.get(this.apiName, '/projects/', {}).then(async (response: any) => {
      // Cas 1 : l’API a retourné une URL vers le JSON sur S3
      if (response.url) {
        const s3Response = await fetch(response.url);
        if (!s3Response.ok) {
          throw new Error(`Erreur lors du chargement des projets depuis S3 : ${s3Response.statusText}`);
        }
        const s3Data = await s3Response.json();
        console.log('Données brutes depuis S3 :', JSON.stringify(s3Data.projects, null, 2));
        return projectSerializer.parseAsArray(s3Data.projects);
      }

      // Cas 2 : l’API a directement retourné les projets
      console.log('Données brutes du backend :', JSON.stringify(response.projects, null, 2));
      return projectSerializer.parseAsArray(response.projects);
    });
  }

  public async createNewProject(project: ProjectCreateRequest): Promise<Project[]> {
    return API.post(this.apiName, '/projects/new', {
      body: project,
    }).then((response: GetProjectResult) => {
      return projectSerializer.parseAsArray(response.projects);
    });
  }

  public async updateProject(payload: ProjectUpdateRequest): Promise<Project[]> {
    const response = await API.post(this.apiName, '/projects/update', {
      body: payload,
    });

    // Cas 1 : l’API retourne une URL vers le fichier JSON (car trop gros)
    if (response.url) {
      const s3Response = await fetch(response.url);
      if (!s3Response.ok) {
        throw new Error(`Erreur lors du chargement des projets depuis S3 : ${s3Response.statusText}`);
      }

      const s3Data = await s3Response.json();
      if (!s3Data.projects || !Array.isArray(s3Data.projects)) {
        throw new Error('Le fichier JSON S3 ne contient pas un tableau "projects" valide');
      }

      console.log('Données brutes depuis S3 après update :', JSON.stringify(s3Data.projects, null, 2));
      return projectSerializer.parseAsArray(s3Data.projects);
    }

    // Cas 2 : l’API retourne directement les projets
    if (!response.projects || !Array.isArray(response.projects)) {
      throw new Error('La réponse API ne contient pas un tableau "projects" valide');
    }

    console.log('Données brutes du backend après update :', JSON.stringify(response.projects, null, 2));
    return projectSerializer.parseAsArray(response.projects);
  }

  public async deleteProject(project: Project): Promise<void> {
    return API.del(this.apiName, `/projects/${project.id}`, {});
  }

  public async loadBlurRegion(projectId: string, cameraId: string): Promise<Blur[]> {
    return API.get(this.apiName, `/projects/${projectId}/${cameraId}/blur`, {}).then((v) =>
      blurSerializer.parseAsArray(v)
    );
  }

  public async saveBlurRegion(projectId: string, cameraId: string, blurs: Blur[]): Promise<void> {
    try {
      console.log('Saving blurs:', JSON.stringify(blurs, null, 2));
      await API.post(this.apiName, `/projects/${projectId}/${cameraId}/blur`, {
        body: { blurs },
      });
    } catch (error) {
      console.error('Error during saveBlurRegion:', error.message);
      console.error('Detailed error information:', error);

      // Afficher plus d'informations sur l'erreur dans la console
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
      }

      // Renvoyer l'erreur si vous souhaitez la propager
      throw error;
    }
  }

  public async getTimelapseConfiguration(projectId: string, cameraId: string): Promise<Video | undefined> {
    return API.get(this.apiName, `/timelapse/${projectId}/${cameraId}`, {}).then(
      (response: GetTimelapseConfigurationResult) => {
        return videoSerializer.parse(response.video);
      }
    );
  }

  public async updateTimelapseConfiguration(
    projectId: string,
    cameraId: string,
    timelapseConfiguration: TimelapseConfiguration
  ): Promise<Video | undefined> {
    return API.post(this.apiName, `/timelapse/${projectId}/${cameraId}`, {
      body: timelapseConfiguration,
    }).then((response: GetTimelapseConfigurationResult) => {
      return videoSerializer.parse(response.video);
    });
  }

  public async getScenarios(projectId: string, cameraId: string): Promise<Scenario[]> {
    return API.get(this.apiName, `/scenario/search?projectId=${projectId}&cameraId=${cameraId}`, {}).then(
      (response: GetScenariosResult) => {
        return scenarioSerializer.parseAsArray(response.scenarios);
      }
    );
  }

  public async scenarioCreateNew(
    projectId: string,
    cameraId: string,
    timelapseConfiguration: TimelapseConfiguration,
    name: string
  ): Promise<Scenario | undefined> {
    const body: ScenarioConfigurationCreateRequest = { timelapseConfiguration, projectId, cameraId, name };
    return API.post(this.apiName, `/scenario/create`, {
      body,
    }).then((response: ScenarioConfigurationCreateResult) => {
      return scenarioSerializer.parse(response.scenario);
    });
  }

  public async scenarioUpdate(
    scenarioId: string,
    timelapseConfiguration: TimelapseConfiguration,
    name: string
  ): Promise<Scenario | undefined> {
    const body: ScenarioConfigurationUpdateRequest = { timelapseConfiguration, name, scenarioId };
    return API.post(this.apiName, `/scenario/${scenarioId}`, {
      body,
    }).then((response: ScenarioConfigurationCreateResult) => {
      return scenarioSerializer.parse(response.scenario);
    });
  }

  public async scenarioDelete(scenarioId: string): Promise<void> {
    return API.post(this.apiName, `/scenario/${scenarioId}/delete`, {});
  }
}
